import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Loader2,
  FileText,
  User,
} from 'lucide-react';
import { auditApi } from '@/api/auditApi';
import type { AuditLogEntry } from '@/types/api';
import type { AuditLogPage } from '@/api/auditApi';
import { Button } from '@/components/common/Button';
import { formatDate } from '@/utils/formatters';
import { EntityLink } from '@/components/detail/EntityLink';

const PAGE_SIZE = 50;

const ACTION_TYPES = [
  'ALL',
  'PRODUCT_CREATED',
  'PRODUCT_UPDATED',
  'SUPPLY_ADJUSTED',
  'SALE_RECORDED',
  'TRANSFER_CREATED',
  'USER_UPDATED',
  'INVENTORY_ADJUSTED',
] as const;

const ENTITY_TYPES = [
  'ALL',
  'PRODUCT',
  'SUPPLY',
  'SALE',
  'TRANSFER',
  'USER',
  'INVENTORY',
] as const;

type DateRange = '7' | '30' | 'all';

const DATE_RANGES: { label: string; value: DateRange }[] = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'All time', value: 'all' },
];

function actionBadgeClass(actionType: string): string {
  const lower = actionType.toLowerCase();
  if (lower.includes('creat') || lower.includes('add')) {
    return 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20';
  }
  if (lower.includes('updat') || lower.includes('edit')) {
    return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20';
  }
  if (lower.includes('delet') || lower.includes('remov')) {
    return 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20';
  }
  if (lower.includes('adjust')) {
    return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20';
  }
  if (lower.includes('transfer')) {
    return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20';
  }
  return 'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border border-gray-300 dark:border-charcoal-600';
}

function formatActionLabel(actionType: string): string {
  return actionType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function JsonViewer({ raw }: { raw: string | null }) {
  if (!raw) return <span className="text-gray-600 dark:text-warm-sand/50 italic text-xs">—</span>;
  try {
    const parsed = JSON.parse(raw);
    return (
      <pre className="text-xs text-gray-600 dark:text-warm-sand bg-white dark:bg-charcoal-900 rounded-lg p-3 overflow-x-auto max-h-48 border border-gray-300 dark:border-charcoal-600 font-mono">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  } catch {
    return (
      <pre className="text-xs text-gray-600 dark:text-warm-sand bg-white dark:bg-charcoal-900 rounded-lg p-3 overflow-x-auto font-mono border border-gray-300 dark:border-charcoal-600">
        {raw}
      </pre>
    );
  }
}

interface ExpandedRow {
  entry: AuditLogEntry;
}

function ExpandedDetails({ entry }: ExpandedRow) {
  return (
    <tr className="bg-white dark:bg-charcoal-900/60">
      <td colSpan={7} className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider mb-2">
              Previous Values
            </p>
            <JsonViewer raw={entry.oldValues} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider mb-2">
              New Values
            </p>
            <JsonViewer raw={entry.newValues} />
          </div>
          {entry.ipAddress && (
            <div className="md:col-span-2">
              <p className="text-xs text-gray-600 dark:text-warm-sand/60">
                IP Address: <span className="mono text-gray-600 dark:text-warm-sand">{entry.ipAddress}</span>
              </p>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export function AuditLog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('ALL');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('ALL');
  const [dateRange, setDateRange] = useState<DateRange>('30');
  const [page, setPage] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => auditApi.getAll(),
  });

  // Normalise response — may be paged object or plain array
  const allEntries = useMemo<AuditLogEntry[]>(() => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData as AuditLogEntry[];
    const paged = rawData as AuditLogPage;
    if (paged.content && Array.isArray(paged.content)) return paged.content;
    return [];
  }, [rawData]);

  // Client-side filtering
  const filteredEntries = useMemo(() => {
    const now = Date.now();
    const cutoff =
      dateRange === 'all'
        ? 0
        : now - parseInt(dateRange) * 24 * 60 * 60 * 1000;

    return allEntries.filter((entry) => {
      const entryTime = new Date(entry.createdAt).getTime();

      const matchesDate = dateRange === 'all' || entryTime >= cutoff;

      const matchesAction =
        actionTypeFilter === 'ALL' ||
        entry.actionType === actionTypeFilter;

      const matchesEntity =
        entityTypeFilter === 'ALL' ||
        entry.entityType === entityTypeFilter;

      const matchesSearch =
        !searchQuery ||
        entry.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.entityId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.actionType?.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesDate && matchesAction && matchesEntity && matchesSearch;
    });
  }, [allEntries, searchQuery, actionTypeFilter, entityTypeFilter, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
  const pagedEntries = filteredEntries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleFilterChange = () => {
    setPage(0);
    setExpandedIds(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream flex items-center gap-3">
            <ShieldCheck className="w-8 h-8 text-amber-450" />
            Audit Log
          </h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Full system activity trail — every change, every action
          </p>
        </div>
        <div className="flex items-center gap-2 text-gray-600 dark:text-warm-sand text-sm">
          <Clock className="w-4 h-4 text-amber-450" />
          <span>
            {filteredEntries.length.toLocaleString()} events
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand" />
            <input
              type="text"
              placeholder="Search descriptions, users, IDs…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleFilterChange();
              }}
              className="input w-full pl-10"
            />
          </div>

          {/* Action Type */}
          <div className="relative sm:w-52">
            <select
              value={actionTypeFilter}
              onChange={(e) => {
                setActionTypeFilter(e.target.value);
                handleFilterChange();
              }}
              className="input w-full appearance-none pr-10"
            >
              {ACTION_TYPES.map((a) => (
                <option key={a} value={a}>
                  {a === 'ALL' ? 'All Actions' : formatActionLabel(a)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand pointer-events-none" />
          </div>

          {/* Entity Type */}
          <div className="relative sm:w-44">
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                handleFilterChange();
              }}
              className="input w-full appearance-none pr-10"
            >
              {ENTITY_TYPES.map((e) => (
                <option key={e} value={e}>
                  {e === 'ALL' ? 'All Entities' : e}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 dark:text-warm-sand pointer-events-none" />
          </div>

          {/* Date Range */}
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-charcoal-600">
            {DATE_RANGES.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => {
                  setDateRange(value);
                  handleFilterChange();
                }}
                className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                  dateRange === value
                    ? 'bg-amber-450/10 text-amber-450 font-medium'
                    : 'bg-white dark:bg-charcoal-800 text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 hover:text-gray-900 dark:text-warm-cream'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Audit Table */}
      <div className="bg-white dark:bg-charcoal-800 rounded-xl border border-gray-200 dark:border-charcoal-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-100 dark:bg-charcoal-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider whitespace-nowrap">
                  Timestamp
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Action
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Entity
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Entity ID
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Description
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 dark:text-warm-sand uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal-700/50">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-600 dark:text-warm-sand">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-450" />
                      <span>Loading audit log…</span>
                    </div>
                  </td>
                </tr>
              ) : pagedEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-600 dark:text-warm-sand">
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="w-10 h-10 opacity-30" />
                      <p>No audit entries found</p>
                      <p className="text-sm opacity-60">Try adjusting your filters or date range</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedEntries.flatMap((entry) => {
                  const isExpanded = expandedIds.has(entry.id);
                  const hasDetails = entry.oldValues || entry.newValues;

                  const rows = [
                    <tr
                      key={entry.id}
                      className={`hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700/30 transition-colors ${
                        isExpanded ? 'bg-gray-100 dark:bg-charcoal-700/20' : ''
                      }`}
                    >
                      {/* Timestamp */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-gray-900 dark:text-warm-cream text-sm mono">
                          {formatDate(entry.createdAt)}
                        </p>
                        <p className="text-gray-600 dark:text-warm-sand/60 text-xs mono">
                          {new Date(entry.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </p>
                      </td>

                      {/* User */}
                      <td className="px-4 py-3">
                        {entry.userName ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-amber-450/10 flex items-center justify-center flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-amber-450" />
                            </div>
                            <span className="text-gray-900 dark:text-warm-cream text-sm">{entry.userName}</span>
                          </div>
                        ) : (
                          <span className="text-gray-600 dark:text-warm-sand/50 text-sm italic">System</span>
                        )}
                      </td>

                      {/* Action Badge */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionBadgeClass(
                            entry.actionType
                          )}`}
                        >
                          {formatActionLabel(entry.actionType)}
                        </span>
                      </td>

                      {/* Entity Type */}
                      <td className="px-4 py-3">
                        <span className="text-gray-600 dark:text-warm-sand text-sm">{entry.entityType || '—'}</span>
                      </td>

                      {/* Entity ID */}
                      <td className="px-4 py-3">
                        {entry.entityId ? (
                          <AuditEntityLink entityType={entry.entityType} entityId={entry.entityId} />
                        ) : (
                          <span className="text-gray-600 dark:text-warm-sand/40 text-sm">—</span>
                        )}
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-gray-900 dark:text-warm-cream text-sm truncate" title={entry.description}>
                          {entry.description || '—'}
                        </p>
                        {entry.locationName && entry.locationId && (
                          <p className="text-xs text-gray-600 dark:text-warm-sand/60 mt-0.5">
                            <EntityLink type="location" id={entry.locationId} label={entry.locationName} className="text-xs font-normal" />
                          </p>
                        )}
                        {entry.locationName && !entry.locationId && (
                          <p className="text-xs text-gray-600 dark:text-warm-sand/60 mt-0.5">{entry.locationName}</p>
                        )}
                      </td>

                      {/* Expand Details */}
                      <td className="px-4 py-3 text-center">
                        {hasDetails ? (
                          <button
                            onClick={() => toggleExpanded(entry.id)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isExpanded
                                ? 'bg-amber-450/10 text-amber-450'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:text-warm-cream'
                            }`}
                            title={isExpanded ? 'Collapse details' : 'Expand details'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-600 dark:text-warm-sand/30 text-xs">—</span>
                        )}
                      </td>
                    </tr>,
                  ];

                  if (isExpanded) {
                    rows.push(<ExpandedDetails key={`${entry.id}-expanded`} entry={entry} />);
                  }

                  return rows;
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!isLoading && filteredEntries.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-800/50">
            <p className="text-sm text-gray-600 dark:text-warm-sand">
              Showing{' '}
              <span className="font-medium text-gray-900 dark:text-warm-cream">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredEntries.length)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-gray-900 dark:text-warm-cream">
                {filteredEntries.length.toLocaleString()}
              </span>{' '}
              entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-gray-600 dark:text-warm-sand text-sm px-2">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditEntityLink({ entityType, entityId }: { entityType: string; entityId: string }) {
  const label = entityId.length > 12 ? `${entityId.slice(0, 8)}…` : entityId;
  const type = entityType?.toUpperCase();

  if (type === 'PRODUCT') {
    return <EntityLink type="product" id={entityId} label="View product" className="text-xs" />;
  }
  if (type === 'LOCATION') {
    return <EntityLink type="location" id={entityId} label="View location" className="text-xs" />;
  }
  if (type === 'SUPPLY') {
    return <EntityLink type="supply" id={entityId} label="View supply" className="text-xs" />;
  }

  return (
    <span
      className="mono text-xs text-gray-600 dark:text-warm-sand/70 bg-gray-100 dark:bg-charcoal-700 px-2 py-0.5 rounded"
      title={entityId}
    >
      {label}
    </span>
  );
}
